import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { useRouter as useRouterNavigation } from 'next/navigation'
import { useRouter } from 'next/router'

import { VaultTokenContextStateType, VaultTokenType } from '@/types/vaultTokensTypes'

const VaultTokensContext = createContext<VaultTokenContextStateType>({
  token: '',
  tokens: [],
  currentToken: null,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  changeToken: (givetoken: string) => null,
})

// AVAX Vault is the flagship and default vault (index 0). GGP Vault remains
// available and reachable at /ggp but is secondary.
const tokens: VaultTokenType[] = [
  {
    name: 'AVAX Vault',
    displayName: 'AVAX',
    value: 'avax',
    image: '/assets/img/nav/avax-vault.svg',
    mainBackgroundColor: '#FFF4F5',
    arrowsBackgroundColor: '#FF394A',
    arrowsColor: '#FFFFFF',
    buttonBackgroundColor: '#000000',
    buttonTextColor: '#FFFFFF',
    buttonVariant: 'avax',
    tokenAddresses: [
      //AVAX
      {
        43113: '',
        43114: '',
      },
      //seaAVAX
      {
        43113: '0x6af383c290d54114327C2087c1983cC9a3bf051d',
        43114: '0x36213ca1483869c5616be738Bf8da7C9B34Ace8d',
      },
    ],
  },
  {
    name: 'GGP Vault',
    displayName: 'GGP',
    value: 'ggp',
    image: '/assets/img/nav/ggp-vault.svg',
    mainBackgroundColor: '#F7F9FF',
    arrowsBackgroundColor: '#00C2FF',
    arrowsColor: '#000000',
    buttonBackgroundColor: '#FFF500',
    buttonTextColor: '#000000',
    buttonVariant: 'primary',
    tokenAddresses: [
      //GGP
      {
        43113: '0xf60E7F4EF0c94ff463863D2826524B2a1431CBD6',
        43114: '0x69260B9483F9871ca57f81A90D91E2F96c2Cd11d',
      },
      //xGGP
      {
        43113: '0x2d79B8cF74D84859818802553D9751cFfeB818C9',
        43114: '0xdF34022e8a280fc79499cA560439Bb6f9797EbD8',
      },
    ],
  },
]

const VaulTokensProvider = ({ children }) => {
  const router = useRouter()
  const nextNavigation = useRouterNavigation()
  const [token, setToken] = useState(tokens[0].value)

  const currentToken = useMemo(() => tokens.find((item) => item.value === token), [token])

  const changeToken = useCallback(
    (givenToken: string) => {
      nextNavigation.push(`/${givenToken}`)
    },
    [nextNavigation],
  )

  useEffect(() => {
    if (router.query.token) {
      if (tokens.find((item) => item.value === router.query.token)) {
        setToken(router.query.token as string)
      } else {
        nextNavigation.replace('/')
      }
    }
  }, [router.query.token, nextNavigation])

  const contextValue = useMemo(
    () => ({
      token,
      tokens,
      currentToken,
      changeToken,
    }),
    [token, currentToken, changeToken],
  )

  return <VaultTokensContext.Provider value={contextValue}>{children}</VaultTokensContext.Provider>
}

const useValueTokens = () => {
  const context = useContext(VaultTokensContext)
  if (!context) {
    throw new Error('useValueTokens might be called inside VaulTokensProvider')
  }
  return context
}

export { useValueTokens, VaulTokensProvider }
